/**
 * TaskVehiclesTab - Aba "Veículos" do modal de detalhes da tarefa
 *
 * Cópia funcional da DealVehiclesTab: lista os veículos vinculados à tarefa
 * (task_vehicles) e permite vincular/desvincular veículos existentes.
 * Toda leitura/escrita passa por RPCs SECURITY DEFINER
 * (list_customer_vehicles / link_task_vehicle).
 *
 * @version 1.1.0
 * RELEASE: 2026-08-09 v1.1.0 - Oculta veículos já vinculados no seletor, coluna "Tipo"
 *            com ícone + palavra (VehicleTypeCell) e clique na linha abre o modal 360º
 * RELEASE: 2026-08-09 v1.0.0 - Criação inicial (múltiplos veículos por tarefa)
 */

import { useState } from 'react';
import { Car, Link2, Loader2, Unlink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { VehicleSelector } from '@/components/tasks/VehicleSelector';
import { VehicleTypeCell } from '@/components/vehicles/VehicleTypeCell';
import { VehicleDetailsModalPremium } from '@/components/vehicles/modal/VehicleDetailsModalPremium';
import { useCustomerVehicles, useTaskVehicleLink } from '@/hooks/useCustomerVehicles';

interface TaskVehiclesTabProps {
  /** ID da tarefa atual */
  taskId: string;
  /** Aba visível (lazy loading das queries) */
  isActive: boolean;
  /** Bloqueia ações de vínculo (tarefa concluída/sem permissão) */
  readOnly?: boolean;
}

/** Formata valor FIPE em BRL */
function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function TaskVehiclesTab({ taskId, isActive, readOnly = false }: TaskVehiclesTabProps) {
  // Veículos já vinculados à tarefa
  const { vehicles: linked, isLoading } = useCustomerVehicles({ taskId }, isActive);

  const linkMutation = useTaskVehicleLink();

  // Veículo selecionado no seletor (busca em todo o tenant)
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Veículo aberto no modal 360º (clique na linha da tabela)
  const [detailsVehicleId, setDetailsVehicleId] = useState<string | null>(null);

  /** Vincula o veículo selecionado à tarefa */
  const handleLink = async () => {
    if (!selectedId) return;
    await linkMutation.mutateAsync({ taskId, vehicleId: selectedId });
    setSelectedId(null);
  };

  /** Remove o vínculo do veículo com a tarefa */
  const handleUnlink = async (vehicleId: string) => {
    await linkMutation.mutateAsync({ taskId, vehicleId, unlink: true });
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Barra de ações: seleciona e vincula um veículo do tenant */}
      {!readOnly && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="sm:max-w-sm w-full">
            <VehicleSelector
              value={selectedId}
              onSelect={setSelectedId}
              placeholder="Buscar veículo por placa, marca ou modelo..."
              excludeIds={linked.map((v) => v.id)}
            />
          </div>

          <Button
            size="sm"
            onClick={handleLink}
            disabled={!selectedId || linkMutation.isPending}
            className="gap-1.5"
          >
            {linkMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="h-4 w-4" />
            )}
            Vincular
          </Button>
        </div>
      )}

      {/* Listagem dos veículos vinculados */}
      {linked.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-10 text-center">
          <Car className="h-8 w-8 text-muted-foreground/60" />
          <div>
            <p className="text-sm font-medium">Nenhum veículo vinculado</p>
            <p className="text-xs text-muted-foreground">
              Vincule um ou mais veículos para acompanhar esta tarefa.
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[680px] text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Placa</th>
                <th className="px-3 py-2 text-left font-medium">Tipo</th>
                <th className="px-3 py-2 text-left font-medium">Marca / Modelo</th>
                <th className="px-3 py-2 text-left font-medium">Ano</th>
                <th className="px-3 py-2 text-right font-medium">Valor FIPE</th>
                {!readOnly && <th className="w-14 px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y">
              {linked.map((vehicle) => (
                <tr
                  key={vehicle.id}
                  className="cursor-pointer hover:bg-muted/30"
                  onClick={() => setDetailsVehicleId(vehicle.id)}
                >
                  <td className="px-3 py-2 font-mono font-medium">{vehicle.plate || '—'}</td>
                  <td className="px-3 py-2">
                    <VehicleTypeCell
                      type={vehicle.vehicle_type}
                      categoryName={vehicle.category_name}
                    />
                  </td>
                  <td className="px-3 py-2">
                    {[vehicle.brand_name, vehicle.model_name].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-3 py-2">{vehicle.year ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(vehicle.fipe_value)}</td>
                  {!readOnly && (
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        title="Desvincular da tarefa"
                        onClick={() => handleUnlink(vehicle.id)}
                        disabled={linkMutation.isPending}
                      >
                        <Unlink className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal 360º do veículo (abre ao clicar na linha) */}
      {detailsVehicleId && (
        <VehicleDetailsModalPremium
          vehicleId={detailsVehicleId}
          open={!!detailsVehicleId}
          onOpenChange={(isOpen) => !isOpen && setDetailsVehicleId(null)}
        />
      )}
    </div>
  );
}
